import Head from 'next/head'
import { useRouter } from 'next/router'
import { useMutation, useQuery } from '@apollo/client'
import NodeCache from 'node-cache'
import { useEffect, useMemo, useState } from 'react'
import dynamic from 'next/dynamic'
// Components
import DiscountDetailModal from '@/components/apply/DiscountDetailModal'
import Footer from '@/components/newFooter'
import Header from '@/components/newHeader/Header'
import Loader from '@/components/newLoader/Loader'
import Pagination from '@/components/newPagination/Pagination'
import PuppyCard from '@/components/newPuppyCard/puppyCard'
import Bar from '@/components/search/Bar'
import ComingSoon from '@/components/search/ComingSoon'
import DontSee from '@/components/search/DontSee'
import Filters from '@/components/search/Filters'
import RelatedBreeds from '@/components/search/RelatedBreeds'
import SortBy from '@/components/search/SortBy'
import SignupModal from '@/components/signupModal'
import Title from '@/components/Title'
import TextBody from '@/components/TextBody/TextBody'
// Utils
import { initializeApollo } from '@/utils/apolloClient'
import useMediaQuery from '@/utils/publicApi'
import { mappingVariables } from '@/utils/search'
import { clearToken, getToken, getUserData } from '@/utils/tokenStorage'
import {
  getPageDescription,
  getPageTitle,
  getPath,
  isFavourite,
  parseSortBy,
  stringifyKey,
} from '@/components/search/lib/utils'
import {
  BREED_DATA_FOR_VALIDATION,
  GET_BREED_DATA,
  GET_CUSTOMER_FAVOURITES,
  GET_FRONTEND_CONFIG_LANDING,
  GET_RANDOM_BREED_DISCOUNT,
  PUPPY_SEARCH,
  UPDATE_FAVOURITE,
} from '@/graphql/index'

import { ROBOTS_META } from 'const'

import styles from './index.module.scss'

const NotificationFormModal = dynamic(
  () => import('@/components/notificationFormModal/notificationFormModal'),
)
const Why = dynamic(() => import('@/components/home/Why'))
const PuppyQuizBanner = dynamic(
  () => import('@/components/search/PuppyQuizBanner'),
)
const CustomerReview = dynamic(
  () => import('@/components/search/CustomerReview'),
)

export default function Search({
  dogsData,
  breedData,
  randomBreedDiscount,
  loadVariables,
  pagination,
  isDiscountBannerClosed,
  setIsDiscountBannerClosed,
}) {
  const { loading: _favouriteLoading, data: _favouriteData } = useQuery(
    GET_CUSTOMER_FAVOURITES,
  )
  // Handles global error
  const [userData, setUserData] = useState(getUserData(getToken()))
  const [showDiscountModal, setShowDiscountModal] = useState(false)
  const [relatedBreeds, setRelatedBreeds] = useState([])
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [selectedBreed, setSelectedBreed] = useState({})
  const [isLoading, setIsLoading] = useState(false)

  const isMobile = useMediaQuery('(max-width:600px)')
  const isTablet = useMediaQuery('(max-width:1270px)')

  // Discount
  const discount =
    breedData?.discount?.[
      Math.max((breedData?.discount || []).length - 1, 0)
    ] || randomBreedDiscount
  const router = useRouter()
  // Router
  const setNewRoute = (...args) => {
    setIsLoading(true)
    const variables = {}

    let key = null
    args.forEach((param) => {
      if (key) {
        variables[key] = param
        key = null
      } else {
        key = param
      }
    })

    const {
      breedId,
      breedSlug,
      category,
      stateName,
      zipcode,
      price,
      size,
      gender,
      hypoallergenic,
      color,
      sort,
    } = loadVariables
    const breed = breedSlug || breedId

    return router
      .push(
        getPath({
          breed,
          category,
          stateName,
          zipcode,
          price,
          size,
          gender,
          hypoallergenic,
          color,
          sort,
          ...variables,
        }),
      )
      .then(() => setIsLoading(false))
  }

  useEffect(() => {
    setSelectedBreed({
      id: breedData?.id,
      name: breedData?.name,
    })
  }, [breedData])

  const getRelatedBreeds = async () => {
    const request = await fetch(`/api/related-breeds/${selectedBreed.id}`)
    const data = await request.json()
    if (data?.relatedBreeds) setRelatedBreeds(data.relatedBreeds?.slice(0, 10))
  }

  useEffect(() => {
    if (breedData?.id) {
      getRelatedBreeds()
    }
  }, [selectedBreed])

  /* HEAD */
  const { pageTitle, dogCategory } = getPageTitle(
    loadVariables?.category,
    breedData?.name,
  )

  /* SIGN UP MODAL */
  const [showSignup, setShowSignup] = useState(false)

  /* NOTIFICATION FORM MODAL */
  const [showNotificationForm, setShowNotificationForm] = useState(false)

  /* PAGINATION */
  const [firstPage, setFirstPage] = useState(pagination?.firstPage)
  const [lastPage, setLastPage] = useState(pagination?.lastPage)
  // Updates first and last pages
  useEffect(() => {
    setFirstPage(pagination?.firstPage)
    setLastPage(pagination?.lastPage)
  }, [pagination])
  // Handlers
  const onPageButtonClick = (n) =>
    setNewRoute('page', n).then(() => {
      const top = isMobile ? 0 : isTablet ? 210 : 150
      window.scrollTo({
        top,
        behavior: 'smooth',
      })
    })
  const onPrevButtonClick = () => {
    if (firstPage >= 3) {
      setFirstPage(firstPage - 2)
      setLastPage(lastPage - 2)
    } else {
      setFirstPage(1)
      setLastPage(lastPage - 1)
    }
  }
  const onNextButtonClick = () => {
    if (Math.ceil(dogsData?.length / pagination?.pageSize) >= lastPage + 2) {
      setFirstPage(firstPage + 2)
      setLastPage(lastPage + 2)
    } else {
      setFirstPage(
        firstPage +
          Math.ceil(dogsData?.length / pagination?.pageSize) -
          lastPage,
      )
      setLastPage(Math.ceil(dogsData?.length / pagination?.pageSize))
    }
  }

  /* SORT BY */
  const [showSortBy, setShowSortBy] = useState(false)

  /* PUPPY CARD */
  const [autosize, setAutosize] = useState(false)
  useEffect(() => setAutosize(isMobile), [isMobile])

  /* FAVOURITES */
  const [favourites, setFavourites] = useState([])
  const [selectedDog, setSelectedDog] = useState(null)

  let favouriteLoading
  let favouriteData
  if (userData) {
    favouriteLoading = _favouriteLoading
    favouriteData = _favouriteData
  }

  useEffect(() => {
    if (favouriteData?.customer?.favourites?.length) {
      setFavourites(favouriteData.customer.favourites)
    }
  }, [favouriteLoading])

  // Update favorite
  const [updateFavourite] = useMutation(UPDATE_FAVOURITE, {
    onCompleted: (response) => {
      if (response.updateFavourite) {
        setFavourites((cur) => {
          const newFavourites = cur
          const res = newFavourites.filter(
            (favourite) => favourite.id !== response.updateFavourite.id,
          )
          if (res.length === newFavourites.length) {
            res.push(response.updateFavourite)
          }
          return res
        })
      }
    },
  })

  // Set open graph Image && url
  let OG_IMAGE = null
  let OG_URL = null
  if (
    Object.entries(breedData || {}).length !== 0 &&
    breedData.constructor === Object
  ) {
    OG_IMAGE = `${process.env.NEXT_PUBLIC_MEDIA_BUCKET}${breedData?.coverPicture?.small}`
    OG_URL = `${process.env.NEXT_PUBLIC_MEDIA_BUCKET}${breedData?.coverPicture?.small}`
  } else {
    OG_IMAGE = `${process.env.NEXT_PUBLIC_ROOT_URL}/mawoo_social_1600x906.png`
    OG_URL = `${process.env.NEXT_PUBLIC_ROOT_URL}/`
  }

  const [showFilters, setShowFilters] = useState(false)
  useEffect(
    () => (isTablet ? setShowFilters(false) : setShowFilters(true)),
    [isTablet],
  )

  const title = useMemo(() => {
    if (dogCategory) return `${dogCategory} Puppies`
    if (breedData?.name) return `${breedData?.name} Puppies`
    return 'Available Puppies '
  }, [dogCategory, breedData?.name])

  return (
    <>
      <Head>
        {/*  Facebook tags  */}
        <meta property="og:title" content={pageTitle} key="title" />
        <meta property="og:type" content="website" key="type" />
        <meta property="og:url" content={OG_URL} key="url" />
        <meta
          name="og:description"
          content={getPageDescription(loadVariables?.category, breedData?.name)}
        />
        <meta property="og:image" content={OG_IMAGE} key="image" />
        <meta property="og:image:alt" content="Search image" key="imageAlt" />
        <meta property="og:locale" content="en_US" key="locale" />
        {/*  twitter tags  */}
        <meta
          name="twitter:card"
          content="summary_large_image"
          key="twitterCard"
        />
        <meta
          name="twitter:title"
          content="Mawoo Pets: Adorable Puppies from Caring Breeders"
          key="twitterTitle"
        />
        <meta
          name="twitter:description"
          content={getPageDescription(loadVariables?.category, breedData?.name)}
          key="twitterDescription"
        />
        <meta name="twitter:image" content={OG_IMAGE} key="twitterImage" />
        {/*  Standard tags  */}
        <title>{pageTitle}</title>
        <meta
          name="description"
          content={getPageDescription(loadVariables?.category, breedData?.name)}
        />
        <link
          rel="canonical"
          href={process.env.NEXT_PUBLIC_ROOT_URL + '/search'}
        />
        <meta name="robots" content={ROBOTS_META} />

        <meta charSet="UTF-8" />
        <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, maximum-scale=1"
        />
      </Head>

      {showNotificationForm && (
        <NotificationFormModal
          setIsSubmitted={() => setIsSubmitted(true)}
          isOpen={showNotificationForm}
          close={() => setShowNotificationForm(false)}
        />
      )}

      <SignupModal
        isOpen={showSignup}
        close={() => setShowSignup(false)}
        success={() => {
          setShowSignup(false)
          setUserData(getUserData(getToken()))
          updateFavourite({
            variables: { dog: selectedDog },
          })
        }}
      />

      {isLoading && !isTablet && (
        <div className={styles.loader}>
          <Loader theme="tall" />
        </div>
      )}

      <main className={styles.main}>
        <Header
          isDiscountBannerClosed={isDiscountBannerClosed}
          setIsDiscountBannerClosed={setIsDiscountBannerClosed}
        />

        <Bar
          selectedBreed={selectedBreed}
          setSelectedBreed={setSelectedBreed}
          selectedCategory={loadVariables?.category}
          selectedZipcode={loadVariables?.zipcode || ''}
          setNewRoute={setNewRoute}
          isTablet={isTablet}
          setShowNotificationForm={setShowNotificationForm}
          // Only needed for mobile
          isMobile={isMobile}
          isDiscountBannerClosed={isDiscountBannerClosed}
        />

        <div className={styles.content}>
          <div className={styles.header}>
            <Title variant="h1">{title}</Title>

            <div className={styles.subtitle}>
              <TextBody size="m">
                <TextBody size="m" variant="span" weight="bold">
                  {dogsData?.length || 0}
                </TextBody>{' '}
                {dogsData?.length === 1 ? 'puppy' : 'puppies'} available
              </TextBody>

              <div className={styles.row}>
                <button
                  className={styles.filters}
                  onClick={() => setShowFilters(true)}
                >
                  <img src={'/filters.svg'} alt="arrow" />

                  <TextBody size="s" weight="bold">
                    Filters
                  </TextBody>
                </button>

                <div
                  className={styles.sort}
                  onClick={() => setShowSortBy(!showSortBy)}
                >
                  <TextBody size="m">
                    <TextBody size="m" variant="span" weight="bold">
                      Sort by:
                    </TextBody>{' '}
                    {parseSortBy(loadVariables?.sort)}
                  </TextBody>

                  <img
                    src={
                      showSortBy
                        ? '/search/arrow-top-black.svg'
                        : '/search/arrow-down-black.svg'
                    }
                    alt="arrow"
                  />

                  {showSortBy && (
                    <div className={styles.options}>
                      <SortBy
                        sortBy={loadVariables?.sort}
                        onClick={(o) => setNewRoute('sort', o)}
                        onClose={() => setShowSortBy(false)}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className={styles.results}>
            {showFilters && (
              <Filters
                price={loadVariables?.price || null}
                size={loadVariables?.size || null}
                gender={loadVariables?.gender || null}
                hypoallergenic={loadVariables?.hypoallergenic || null}
                color={loadVariables?.color || null}
                variables={loadVariables}
                setNewRoute={setNewRoute}
                cardsAmount={dogsData?.sortedDogs?.length}
                setShowFilters={setShowFilters}
                isTablet={isTablet}
              />
            )}

            <div className={styles.rightPanel}>
              {!!dogsData?.sortedDogs?.length && (
                <div className={styles.cardsContainer}>
                  {dogsData?.sortedDogs.map((d) => (
                    <PuppyCard
                      key={d.id}
                      dog={d}
                      state={loadVariables?.stateName}
                      isFavourite={isFavourite(favourites, d.id)}
                      updateFavourite={() => {
                        if (userData) {
                          updateFavourite({
                            variables: { dog: d.id },
                          })
                        } else {
                          setShowSignup(true)
                          setSelectedDog(d.id)
                        }
                      }}
                      autosize={autosize}
                    />
                  ))}
                  {/* DESKTOP */}
                  {!!(dogsData?.sortedDogs?.length % (isMobile ? 2 : 3)) && (
                    <div className={styles.featuredCardNotification}>
                      {isSubmitted ? (
                        <div className={styles.submitted}>
                          <img src="/signin/bubble_icon.svg" alt="icon" />
                          <div className={styles.text}>
                            <h3>Thank you!</h3>
                            <p>
                              We will notify you as soon as we have puppies
                              matching your preferences.
                            </p>
                          </div>
                        </div>
                      ) : (
                        <>
                          <h3>
                            Get notified when{' '}
                            {breedData?.name ? breedData?.name : 'more'} puppies
                            become available.
                          </h3>
                          <button onClick={() => setShowNotificationForm(true)}>
                            <img
                              src="/form_modal/alert.svg"
                              alt="notification bell"
                            />{' '}
                            Notify Me
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}

              {dogsData?.sortedDogs?.length <= 36 &&
                dogsData?.length <= 36 &&
                !!Object.keys(loadVariables).length &&
                !(
                  Object.keys(loadVariables).length === 1 &&
                  loadVariables?.breedSlug
                ) && (
                  <DontSee
                    variables={loadVariables}
                    breed={breedData?.name}
                    setNewRoute={setNewRoute}
                    data={dogsData?.sortedDogs}
                    areDogsThere={!!dogsData?.sortedDogs?.length}
                    isMobile={isMobile}
                    isSubmitted={isSubmitted}
                    setShowNotificationForm={setShowNotificationForm}
                  />
                )}

              {!dogsData?.length &&
                Object.keys(loadVariables).length === 1 &&
                !!loadVariables?.breedSlug && (
                  <ComingSoon breed={breedData?.name} isMobile={isMobile} />
                )}
            </div>
          </div>

          {Math.ceil(dogsData?.length / pagination?.pageSize) > 1 && (
            <Pagination
              pages={Math.ceil(dogsData?.length / pagination?.pageSize)}
              currentPage={pagination?.currentPage}
              firstPage={firstPage}
              lastPage={lastPage}
              onPageButtonClick={onPageButtonClick}
              onPrevButtonClick={onPrevButtonClick}
              onNextButtonClick={onNextButtonClick}
            />
          )}
        </div>

        {!!relatedBreeds?.length && (
          <div className={styles.relatedBreeds}>
            <RelatedBreeds
              title="Puppies from Related Breeds"
              relatedBreeds={relatedBreeds.filter(
                (breed) => breed.dogs.length !== 0,
              )}
              state={false}
              favourites={favourites}
              updateFavourite={(id) => {
                if (userData) {
                  updateFavourite({
                    variables: { dog: id },
                  })
                } else {
                  setShowSignup(true)
                  setSelectedDog(id)
                }
              }}
              isMobile={isMobile}
            />
          </div>
        )}

        <div className={styles.content}>
          <PuppyQuizBanner />
        </div>

        {!!discount?.amount && (
          <div className={styles.content}>
            <DiscountDetailModal
              setShowModal={(value) => setShowDiscountModal(value)}
              showModal={showDiscountModal}
              discount={discount}
            />
          </div>
        )}

        <Why />

        <div className={styles.content}>
          <div className={styles.reviews}>
            <CustomerReview />
          </div>
        </div>

        <Footer />
      </main>
    </>
  )
}

const cache = new NodeCache({ stdTTL: 1 * 60 * 60, checkperiod: 60 }) // Cache 1 hour cache

export async function getServerSideProps({
  res,
  query: _query,
  resolvedUrl,
  params,
}) {
  try {
    const apolloClient = initializeApollo()
    // Ignore cookies
    clearToken()
    if (params?.vars) {
      // Handle redirect for breed, breed Id or category with params
      if (
        params?.vars[0] === 'breed' ||
        params?.vars[0] === 'breedId' ||
        params?.vars[0] === 'category'
      ) {
        return {
          redirect: {
            destination: '/puppies-for-sale/' + params?.vars[1],
            statusCode: 301,
          },
        }
      } else {
        let redirectPath = '/search?'
        // Handle redirect when breed or category is missing
        params.vars?.forEach((param, i) => {
          if (i % 2 && param !== params?.vars[params.vars?.length - 1]) {
            redirectPath = redirectPath.concat(`${param}&`)
          } else if (param === params?.vars[params.vars?.length - 1]) {
            redirectPath = redirectPath.concat(param)
          } else {
            redirectPath = redirectPath.concat(`${param}=`)
          }
        })

        return {
          redirect: {
            destination: redirectPath,
            statusCode: 301,
          },
        }
      }
    }
    if (_query.breed === 'cavadoodles') {
      return {
        redirect: {
          destination: 'search?breed=cavapoo',
          statusCode: 301,
        },
      }
    }
    // GET BREEDS
    let breedsData
    breedsData = cache.get('breeds')
    if (breedsData === undefined) {
      const { data } = await apolloClient.query({
        query: BREED_DATA_FOR_VALIDATION,
      })
      breedsData = data?.breeds || []
      cache.set('breeds', data?.breeds)
    }

    const mapVars = mappingVariables({ breedsData })
    const pagination = { pageSize: 36 }
    const errorType = ''

    // validate key and value pairs
    for (const key in _query) {
      if (mapVars[key]?.isValid(mapVars[key].convert(_query[key]))) {
        _query[key] = mapVars[key].convert(_query[key])
        if (key === 'state') _query.stateName = _query[key]
        if (key === 'breed') {
          typeof _query[key] === 'string'
            ? (_query.breedSlug = _query[key])
            : (_query.breedId = _query[key])
        }
      }
      // Handle pagination
      if (key === 'page') {
        pagination.currentPage = +_query[key]
      }
    }
    let _dogsData = []
    let breedData = {}
    let randomBreedDiscount = null
    let frontendConfigData

    if (res.statusCode !== 404) {
      // SELECTED BREED // check validate by server side render
      if (_query.breedId || _query.breed) {
        breedData = cache.get(
          `breedId ${_query.breedId} - ${_query.breed} related breeds`,
        )
        const tempBreed = breedsData.find((b) => b.slug === _query.breed)
        if (!breedData) {
          const { data } = await apolloClient.query({
            query: GET_BREED_DATA,
            variables: {
              id: _query.breedId || tempBreed.id,
              slug: _query.breedSlug,
            },
          })
          breedData = data?.breed

          cache.set(
            `breedId ${_query.breedId} - ${_query.breed} related breeds`,
            data?.breed,
          )
        }
      }

      let configKey = 'landing-photos'

      if (breedData?.id) {
        configKey = `breed-${breedData?.id}`
      } else if (_query?.category) {
        configKey = `category-${_query?.category}`
      }

      if (!frontendConfigData) {
        const { data: _frontendConfigData } = await apolloClient.query({
          query: GET_FRONTEND_CONFIG_LANDING,
          variables: {
            key: configKey,
          },
        })
        frontendConfigData = _frontendConfigData?.frontendConfig?.value || '[]'
        _query.frontendConfigData = frontendConfigData || []
        cache.set(configKey, frontendConfigData)
      }
      _query.page = pagination.currentPage ? pagination.currentPage - 1 : 0
      // DOGS
      const key = stringifyKey(_query)
      _dogsData = cache.get(key)

      if (!_dogsData) {
        const {
          data: { search },
        } = await apolloClient.query({
          query: PUPPY_SEARCH,
          variables: {
            ..._query,
            sort: _query.sort || 'by-view-score',
            limit: 36,
          },
        })

        _dogsData = search?.rows ? search : { rows: [], count: 0 }
        cache.set(key, _dogsData)
      }

      // PAGINATION
      const latestPage = Math.ceil(_dogsData?.count / pagination.pageSize) || 1
      if (!pagination.currentPage) {
        pagination.currentPage = 1
      } else {
        if (pagination.currentPage > latestPage) {
          pagination.currentPage = latestPage
        }
      }
      // Sets first page
      if (pagination.currentPage - 2 >= 1) {
        pagination.firstPage = pagination.currentPage - 2
      } else {
        pagination.firstPage = 1
      }
      // Sets last page
      if (pagination.currentPage + 2 <= latestPage) {
        pagination.lastPage = pagination.currentPage + 2
      } else {
        pagination.lastPage = latestPage
      }

      if (randomBreedDiscount === null) {
        const { data } = await apolloClient.query({
          query: GET_RANDOM_BREED_DISCOUNT,
        })
        randomBreedDiscount = data?.randomBreedDiscount || {}
        cache.set(
          `breedId ${_query.breedId} - ${_query.breed} related breeds`,
          data?.breed,
        )
      }
    }

    if (_query.breedId && breedData) {
      const newPath = resolvedUrl.replace(_query.breedId, breedData.slug)
      return {
        redirect: {
          source: `/search?breed=${_query.breedId}`,
          destination: newPath,
          statusCode: 301,
        },
      }
    }

    const dogsData = {
      sortedDogs: _dogsData.rows,
      length: _dogsData.count,
    }

    const queryVariables = { ..._query, sort: _query.sort || 'by-view-score' }

    return {
      props: {
        randomBreedDiscount,
        dogsData,
        breedData,
        loadVariables: queryVariables,
        pagination,
        errorType,
      },
    }
  } catch (err) {
    console.error(err)
    throw err
  }
}
